import * as Sentry from '@sentry/react'
import { Loader2, ThumbsDown, ThumbsUp } from 'lucide-react'
import type { ComponentProps } from 'react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/context/AuthContext'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'
import { isSentrySendPiiEnabled, isSentryUserFeedbackAvailable } from '@/services/sentry'

export type FeedbackRating = 'thumbs-up' | 'thumbs-down'

function buildFeedbackMessage(params: {
	rating: FeedbackRating
	component: string
	comment: string
}): string {
	const trimmed = params.comment.trim()
	return `## rating: ${params.rating}\n## component: ${params.component}\n## comment: ${trimmed}`
}

export interface FeedbackBadgeProps {
	/** Stable id for this UI surface (parsed from the Sentry message). */
	component: string
	className?: string
	size?: 'sm' | 'md'
	side?: ComponentProps<typeof PopoverContent>['side']
	sideOffset?: number
}

/** Thumbs up/down badges that send structured feedback via Sentry (`captureFeedback`). */
export function FeedbackBadge({
	component,
	className,
	size = 'sm',
	side = 'top',
	sideOffset = 6,
}: FeedbackBadgeProps) {
	const { t } = useTranslation()
	const { user } = useAuth()
	const [open, setOpen] = useState(false)
	const [rating, setRating] = useState<FeedbackRating>('thumbs-up')
	const [comment, setComment] = useState('')
	const [submitting, setSubmitting] = useState(false)

	if (!isSentryUserFeedbackAvailable()) {
		return null
	}

	const openWithRating = (r: FeedbackRating) => {
		setRating(r)
		setOpen(true)
	}

	const handleSubmit = () => {
		setSubmitting(true)
		try {
			const message = buildFeedbackMessage({ rating, component, comment })
			const sendPii = isSentrySendPiiEnabled()
			const name =
				sendPii && user
					? [user.firstname, user.lastname].filter(Boolean).join(' ') || undefined
					: undefined
			const email = sendPii && user ? user.email : undefined

			const eventId = Sentry.captureFeedback({
				message,
				...(name ? { name } : {}),
				...(email ? { email } : {}),
				url: window.location.href,
				source: 'feedback-badge',
				tags: {
					feedback_component: component,
					feedback_rating: rating,
					feedback_source: 'feedback-badge',
				},
			})

			if (!eventId) {
				toast.error(t('feedbackBadge.submitError'))
				return
			}

			toast.success(t('feedbackBadge.submitSuccess'))
			setComment('')
			setRating('thumbs-up')
			setOpen(false)
		} catch {
			toast.error(t('feedbackBadge.submitError'))
		} finally {
			setSubmitting(false)
		}
	}

	const sizeClasses =
		size === 'sm'
			? 'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'
			: 'text-xs font-medium text-muted-foreground'

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverAnchor asChild>
				<div className={cn('inline-flex max-w-full shrink-0 items-center gap-1', className)}>
					<Tooltip>
						<TooltipTrigger asChild>
							<Badge variant="outline" asChild className={cn('p-0', sizeClasses)}>
								<button
									type="button"
									className="inline-flex size-full items-center justify-center px-1.5 py-0.5"
									aria-label={t('feedbackBadge.thumbsUpAria')}
									disabled={submitting}
									onClick={() => openWithRating('thumbs-up')}
								>
									<ThumbsUp className="size-3" aria-hidden />
								</button>
							</Badge>
						</TooltipTrigger>
						<TooltipContent side={side} sideOffset={sideOffset} className="max-w-xs text-xs">
							{t('feedbackBadge.thumbsUpTooltip')}
						</TooltipContent>
					</Tooltip>

					<Tooltip>
						<TooltipTrigger asChild>
							<Badge variant="outline" asChild className={cn('p-0', sizeClasses)}>
								<button
									type="button"
									className="inline-flex size-full items-center justify-center px-1.5 py-0.5"
									aria-label={t('feedbackBadge.thumbsDownAria')}
									disabled={submitting}
									onClick={() => openWithRating('thumbs-down')}
								>
									<ThumbsDown className="size-3" aria-hidden />
								</button>
							</Badge>
						</TooltipTrigger>
						<TooltipContent side={side} sideOffset={sideOffset} className="max-w-xs text-xs">
							{t('feedbackBadge.thumbsDownTooltip')}
						</TooltipContent>
					</Tooltip>
				</div>
			</PopoverAnchor>

			<PopoverContent
				side={side}
				sideOffset={sideOffset}
				align="center"
				className="w-[min(20rem,calc(100vw-2rem))] space-y-3"
				onOpenAutoFocus={(e) => e.preventDefault()}
			>
				<div className="space-y-1">
					<div className="flex items-center gap-2 text-sm font-medium">
						{rating === 'thumbs-up' ? (
							<ThumbsUp className="size-4 shrink-0 text-muted-foreground" aria-hidden />
						) : (
							<ThumbsDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
						)}
						<span>{t('feedbackBadge.popoverTitle')}</span>
					</div>
					<p className="text-xs text-muted-foreground">{t('feedbackBadge.popoverSubtitle')}</p>
				</div>

				<Textarea
					value={comment}
					onChange={(e) => setComment(e.target.value)}
					placeholder={t('feedbackBadge.commentPlaceholder')}
					rows={3}
					maxLength={1000}
					disabled={submitting}
					aria-label={t('feedbackBadge.commentPlaceholder')}
				/>

				<div className="flex justify-end gap-2">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						disabled={submitting}
						onClick={() => setOpen(false)}
					>
						{t('feedbackBadge.cancel')}
					</Button>
					<Button type="button" size="sm" disabled={submitting} onClick={handleSubmit}>
						{submitting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
						{t('feedbackBadge.submit')}
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}
